import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DateRange, Statement, User } from '@models';
import { StatementService, UtilService } from '@services';
import { PaymentUtils } from '@utils';

@Component({
  selector: 'app-user-statements',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatTooltipModule,
    MatButtonModule,
    MatTableModule,
    MatProgressSpinnerModule,
    MatPaginatorModule,
    MatIconModule,
    FormsModule,
    MatInputModule,
  ],
  templateUrl: './statements.component.html',
  styleUrl: './statements.component.scss',
})
export class UserStatementsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() user: User | null = null;
  @Input() statementId: string | null = null;
  @Output() statementClicked = new EventEmitter<string>();

  public showStatement: boolean = false;
  public statementPdfUrl: SafeResourceUrl | null = null;
  public statementColumns: string[] = [
    'date',
    'income',
    'expenses',
    'total',
    'status',
  ];

  public loading: boolean = true;
  public pageSize: number = 20;
  public pageIndex: number = 0;
  public totalStatements: number = 0;
  public statements: Statement[] = [];

  constructor(
    private sanitizer: DomSanitizer,
    public utils: UtilService,
    private statementService: StatementService
  ) {}

  ngOnInit(): void {
    this.fetchUserStatements();
  }

  ngOnDestroy(): void {
    this.showStatement = false;
    this.statementPdfUrl = null;
    this.statementClicked.emit('');
  }

  ngOnChanges(): void {
    this.showStatementDetails();
  }

  async fetchUserStatements(): Promise<void> {
    this.loading = true;
    try {
      const response = await this.statementService.getPage({
        where: { userId: this.user!.id! },
        offset: this.pageIndex * this.pageSize,
        limit: this.pageSize,
      });
      this.totalStatements = response.count;
      this.statements = response.data;
    } catch (error) {
      console.error('Error occurred during fetching statements page', {
        cause: error,
      });
    }
    this.loading = false;
  }

  public onPageChange(e: PageEvent): void {
    this.pageSize = e.pageSize;
    this.pageIndex = e.pageIndex;
    this.fetchUserStatements();
  }

  showStatementDetails() {
    if (this.statementId) {
      const statement = this.statements?.find(
        (statement) => statement.id === this.statementId
      );
      if (statement) {
        this.showStatementPDF(statement.fileUrl);
      }
    }
  }

  getDateRange(from: Date, to: Date): DateRange {
    const formatDate = (date: Date): string => date.toISOString().split('T')[0];
    return {
      from: formatDate(from),
      to: formatDate(to),
    };
  }

  getTotalIncome(statement: Statement) {
    if (!statement.income) return 0;
    return statement.income.reduce((acc, income) => acc + income.total, 0);
  }

  getTotalExpenses(statement: Statement) {
    if (!statement.expenses) return 0;
    return statement.expenses.reduce((acc, expense) => acc + expense.total, 0);
  }

  getTotal(statement: Statement) {
    return this.getTotalIncome(statement) - this.getTotalExpenses(statement);
  }

  showStatementPDF(fileUrl: string) {
    const safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(fileUrl);
    this.statementPdfUrl = safeUrl;
    this.showStatement = true;
  }

  onViewStatementClicked(statement: Statement) {
    if (!statement.id) return;
    this.statementClicked.emit(statement.id);
  }

  onBackToStatementsClicked() {
    this.showStatement = false;
    this.statementPdfUrl = null;
    this.statementClicked.emit('');
  }

  isPaid(statement: Statement): boolean {
    return PaymentUtils.isPaid(statement.status);
  }

  getStatusText(statement: Statement): string {
    return PaymentUtils.getStatusText(statement.status);
  }
}
